import { useEffect } from "react";

// Phase 25.16i — Bespoke ABM page hardening.
//
// Imperatively set <meta name="robots" content="noindex, nofollow"> on
// mount and clean up on unmount, so search engines never index pages
// that surface per-prospect tactical thinking (e.g. /ventia,
// /strategic-planning-institute, /iwg). The robots.txt covers the
// crawl-budget rule; this meta tag covers the case where a competitor
// shares the URL with another bot or a search engine has already
// discovered the page through a referrer.
//
// Defence in depth: robots.txt is a hint, noindex meta is the binding
// instruction. Both layers required.
//
// Usage (inside any prospect-specific page component):
//   useNoIndex();
//
// Side-effects:
//   - Sets / replaces meta[name="robots"] in document <head>
//   - Sets / replaces meta[name="googlebot"] in document <head>
//   - Cleans both up on unmount so navigating away restores indexability
//     for the next page (public pages stay indexable).
export default function useNoIndex() {
  useEffect(() => {
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    const robots = setMeta("robots", "noindex, nofollow, noarchive, nosnippet");
    const googlebot = setMeta("googlebot", "noindex, nofollow, noarchive, nosnippet");

    return () => {
      // Restore prior state. If we created the tag, remove it. If it
      // existed already with a different value, restore that value.
      if (robots.created) robots.el.remove();
      else if (robots.prev != null) robots.el.setAttribute("content", robots.prev);

      if (googlebot.created) googlebot.el.remove();
      else if (googlebot.prev != null) googlebot.el.setAttribute("content", googlebot.prev);
    };
  }, []);
}
